import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle, Warning } from "@phosphor-icons/react";
import { fmtDate } from "@/lib/fmt";
import { EVENT_COLORS, formatRange } from "@/lib/calendar";

export default function AssignRideModal({ open, onClose, ride, onDone }) {
  const [drivers, setDrivers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Compute the expected ride window to check for conflicts. Mirrors the
  // backend heuristic (airport → 3h, else 2h).
  const rideWindow = useMemo(() => {
    if (!ride) return null;
    try {
      const d = ride.pickup_date;
      const t = (ride.pickup_time || "00:00").padStart(5, "0");
      const start = new Date(`${d}T${t}:00`);
      if (isNaN(start.getTime())) return null;
      const hay = `${ride.route_label || ""} ${ride.info_block || ""}`.toLowerCase();
      const airport = /a[eé]roport|airport|cdg|ory|bva|roissy|orly|beauvais|charles de gaulle/.test(hay);
      const end = new Date(start.getTime() + (airport ? 3 : 2) * 60 * 60 * 1000);
      return { start: start.toISOString(), end: end.toISOString() };
    } catch { return null; }
  }, [ride]);

  useEffect(() => {
    if (open) {
      api.get("/drivers").then((r) => {
        const allDrivers = r.data;
        const interested = ride?.interested_drivers || [];
        const sorted = [
          ...allDrivers.filter((d) => interested.includes(d.id)),
          ...allDrivers.filter((d) => !interested.includes(d.id)),
        ];
        setDrivers(sorted);
        setSelectedId(null);
        setConflicts([]);
      }).catch(() => {});
    }
  }, [open, ride]);

  // Re-check conflicts whenever the selected driver changes.
  useEffect(() => {
    if (!selectedId || !rideWindow) { setConflicts([]); return; }
    setCheckingConflicts(true);
    const params = new URLSearchParams({
      driver_id: selectedId,
      start: rideWindow.start,
      end: rideWindow.end,
    });
    if (ride?.id) params.set("ignore_ride_id", ride.id);
    api.get(`/calendar/conflicts?${params.toString()}`)
      .then((r) => setConflicts(Array.isArray(r.data) ? r.data : []))
      .catch(() => {
        // Phase N1A — do NOT clear `conflicts` on fetch failure: an
        // empty conflicts list silently tells the admin "no conflict,
        // safe to assign" when in reality we couldn't check. Keep the
        // last known list (worst case: stale data, but at least the
        // admin sees something).
      })
      .finally(() => setCheckingConflicts(false));
  }, [selectedId, rideWindow, ride?.id]);

  const handleAssign = async () => {
    if (!selectedId) { toast.error("Select a driver"); return; }
    setLoading(true);
    try {
      await api.put(`/rides/${ride.id}/assign`, { driver_id: selectedId });
      toast.success("Ride assigned");
      onDone?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign");
    } finally {
      setLoading(false);
    }
  };

  const interested = ride?.interested_drivers || [];

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[#121212] border-zinc-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle>Assign Ride</DialogTitle>
          <DialogDescription className="text-zinc-500">{ride?.route_label} - {fmtDate(ride?.pickup_date)} {ride?.pickup_time}</DialogDescription>
        </DialogHeader>
        {interested.length > 0 && (
          <p className="text-xs text-emerald-500 uppercase tracking-wider font-medium">{interested.length} driver{interested.length > 1 ? "s" : ""} interested</p>
        )}
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {drivers.length === 0 && <p className="text-zinc-500 text-sm">No drivers available.</p>}
          {drivers.map((d) => (
            <button key={d.id} data-testid={`assign-driver-${d.id}`} onClick={() => setSelectedId(d.id)}
              className={`w-full flex items-center gap-3 p-3 rounded border transition-colors text-left ${selectedId === d.id ? "bg-amber-500/10 border-amber-500" : "bg-[#1A1A1A] border-zinc-800 hover:border-zinc-600"}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-white truncate">{d.name}</p>
                  {interested.includes(d.id) && <CheckCircle size={16} weight="fill" className="text-emerald-500 shrink-0" />}
                </div>
                <p className="text-xs text-zinc-500">{d.vehicle_model} {d.vehicle_year}</p>
              </div>
              {interested.includes(d.id) && <span className="text-xs text-emerald-500 font-medium shrink-0">Interested</span>}
            </button>
          ))}
        </div>
        {selectedId && (
          <ConflictNotice conflicts={conflicts} checking={checkingConflicts} />
        )}
        <Button data-testid="assign-confirm-btn" onClick={handleAssign} disabled={loading || !selectedId} className="w-full bg-amber-500 text-black font-semibold hover:bg-amber-600 active:scale-[0.98] h-11">
          {loading ? "Assigning..." : conflicts.length > 0 ? "Attribuer quand même" : "Assign Driver"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function ConflictNotice({ conflicts, checking }) {
  if (checking) return <p className="text-xs text-zinc-500">Vérification du planning…</p>;
  if (!conflicts || conflicts.length === 0) {
    return <p className="text-xs text-emerald-500 flex items-center gap-1.5"><CheckCircle size={12} /> Aucun conflit détecté</p>;
  }
  return (
    <div data-testid="assign-conflict-warning" className="rounded-md border border-amber-500/50 bg-amber-500/10 p-3 space-y-2">
      <p className="text-xs font-semibold text-amber-300 flex items-center gap-1.5 uppercase tracking-wider">
        <Warning size={14} weight="fill" /> Attention : ce chauffeur a déjà un créneau occupé
      </p>
      <ul className="space-y-1">
        {conflicts.map((c) => {
          const color = EVENT_COLORS[c.event_type] || EVENT_COLORS.personal_ride;
          const label = c.event_type === "sdrive_ride" ? (c.route_label || "Course dispatch")
            : c.event_type === "personal_ride" ? (c.client_name || c.pickup_location || "Course personnelle")
            : (c.notes || "Indisponible");
          return (
            <li key={c.id} className="flex items-start gap-2 text-[11px] text-amber-100">
              <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${color.dot}`} />
              <span className="flex-1"><span className="font-medium">{color.label}</span> — {label} <span className="text-amber-300/80">({formatRange(c.start_datetime, c.end_datetime)})</span></span>
            </li>
          );
        })}
      </ul>
      <p className="text-[11px] text-amber-200/80">L'attribution reste possible — vérifiez simplement qu'il n'y a pas d'erreur.</p>
    </div>
  );
}

